import { Button, Flex, HtmlHeader, Inline, Main, Tooltip, TooltipTrigger } from "@hopper-ui/components";
import { DismissIcon } from "@hopper-ui/icons";
import { PageLoader, ShareGateWordmarkLogo } from "@sg-protect/components";
import { MAIN_CONTENT_ID, SkipToMain } from "@sg-protect/shell";
import { Suspense, useMemo, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router";
import { usePaths } from "../../paths.ts";
import { resolveReviewWizardExitTarget } from "../../utils/reviewWizardNavigation.ts";
import { SavedIndicator } from "../reviews/SavedIndicator.tsx";
import { FlaggedUserDetailsCardProvider } from "./FlaggedUserDetailsCardProvider.tsx";
import { ModuleDataProviders } from "./ModuleDataProviders.tsx";
import { ModuleGates } from "./ModuleGates.tsx";

const HEADER_HEIGHT = "4.5rem";

export type SaveStatus = "idle" | "saving" | "error";

export interface ReviewWizardOutletContext {
    setSaveStatus: (status: SaveStatus) => void;
    setLastSavedAt: (lastSavedAt: string) => void;
}

export function ReviewCreationWizardLayout() {
    const paths = usePaths();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
    const [lastSavedAt, setLastSavedAt] = useState<string>();
    const outletContext = useMemo<ReviewWizardOutletContext>(() => ({ setSaveStatus, setLastSavedAt }), []);
    const exitTarget = resolveReviewWizardExitTarget(searchParams, paths.reviews);

    return (
        <Flex direction="column" flex="1 1 0" minHeight={0}>
            <SkipToMain href={`#${MAIN_CONTENT_ID}`} />
            <HtmlHeader
                position="sticky"
                top="0"
                zIndex={10}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                UNSAFE_paddingX="0.75rem"
                UNSAFE_height={HEADER_HEIGHT}
                borderBottom="neutral-weak"
                backgroundColor="neutral"
            >
                <ShareGateWordmarkLogo />
                <Inline gap="inline-sm">
                    {lastSavedAt && <SavedIndicator saveStatus={saveStatus} lastSavedAt={lastSavedAt} />}
                    <TooltipTrigger>
                        <Button aria-label="Close" variant="ghost-secondary" onPress={() => navigate(exitTarget)}>
                            <DismissIcon />
                        </Button>
                        <Tooltip data-public>Exit review creation</Tooltip>
                    </TooltipTrigger>
                </Inline>
            </HtmlHeader>
            <Main
                id={MAIN_CONTENT_ID}
                position="relative"
                display="flex"
                flexDirection="column"
                flex="1 1 0"
                minHeight={0}
                overflow="auto"
                width="100%"
            >
                <Suspense fallback={<PageLoader aria-label="Loading page…" />}>
                    <ModuleDataProviders>
                        <FlaggedUserDetailsCardProvider>
                            <ModuleGates>
                                <Outlet context={outletContext} />
                            </ModuleGates>
                        </FlaggedUserDetailsCardProvider>
                    </ModuleDataProviders>
                </Suspense>
            </Main>
        </Flex>
    );
}
